import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectCartCount } from '../../features/cart/cartSlice';
import './StorefrontBottomNav.css';

/**
 * Mobile floating bottom dock for the storefront. Visible only at
 * <=768px (CSS handles the breakpoint). Four thumb-reachable controls:
 *
 *   • Home       — store landing
 *   • Shop       — full product grid
 *   • Search     — opens /shop with the search input focused
 *   • Cart       — opens the CheckoutModal via the onOpenCart callback,
 *                  matching the header cart icon. Shows the live cart
 *                  count as a small badge.
 *
 * Why 4 and not 5: every item needs a clear role. Adding Wishlist or
 * Account without dedicated routes would create dead-end taps. We can
 * grow this in S4 once a Search overlay + customer profile route ship.
 *
 * The dock is themed via --sf-* tokens (see StorefrontBottomNav.css)
 * so it inherits dark/light mode automatically.
 */

const isOnSubdomain = () => {
  const hostname = window.location.hostname;
  return hostname.includes('.') && !hostname.startsWith('www.');
};

const StorefrontBottomNav = ({ store, onOpenCart }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const cartCount = useSelector(selectCartCount);

  // Mirror the path-builder in Header so subdomain + path-based routing
  // both work without duplicating logic at every call site.
  const getStorePath = (path = '') => {
    if (isOnSubdomain()) return path ? `/${path}` : '/';
    if (!store?.slug) return '/';
    return path ? `/s/${store.slug}/${path}` : `/s/${store.slug}`;
  };

  // Active matcher — same semantics as Header.isActive().
  const onSubdomain = isOnSubdomain();
  const isActive = (path) => {
    if (path === '') {
      return onSubdomain
        ? location.pathname === '/'
        : (location.pathname.endsWith('/s/' + store?.slug) ||
            location.pathname.endsWith('/s/' + store?.slug + '/'));
    }
    return location.pathname.includes('/' + path);
  };

  const handleNav = (path, options = {}) => {
    const target = getStorePath(path);
    navigate(options.search ? `${target}${options.search}` : target);
  };

  return (
    <nav className="sf-bottom-nav" aria-label="Primary">
      {/* Home */}
      <button
        type="button"
        className={`sf-bottom-nav__item ${isActive('') ? 'is-active' : ''}`}
        onClick={() => handleNav('')}
        aria-label="Home"
        aria-current={isActive('') ? 'page' : undefined}
      >
        <span className="sf-bottom-nav__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1V9.5z" />
          </svg>
        </span>
        <span className="sf-bottom-nav__label">Home</span>
      </button>

      {/* Shop */}
      <button
        type="button"
        className={`sf-bottom-nav__item ${isActive('shop') ? 'is-active' : ''}`}
        onClick={() => handleNav('shop')}
        aria-label="Shop"
        aria-current={isActive('shop') ? 'page' : undefined}
      >
        <span className="sf-bottom-nav__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7" rx="1.5" />
            <rect x="14" y="3" width="7" height="7" rx="1.5" />
            <rect x="3" y="14" width="7" height="7" rx="1.5" />
            <rect x="14" y="14" width="7" height="7" rx="1.5" />
          </svg>
        </span>
        <span className="sf-bottom-nav__label">Shop</span>
      </button>

      {/* Search — opens shop with the search input focused. */}
      <button
        type="button"
        className="sf-bottom-nav__item"
        onClick={() => handleNav('shop', { search: '?focusSearch=1' })}
        aria-label="Search"
      >
        <span className="sf-bottom-nav__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
        </span>
        <span className="sf-bottom-nav__label">Search</span>
      </button>

      {/* Cart — opens CheckoutModal so behavior mirrors the header. */}
      <button
        type="button"
        className="sf-bottom-nav__item"
        onClick={() => onOpenCart && onOpenCart()}
        aria-label={cartCount > 0 ? `Cart, ${cartCount} item${cartCount === 1 ? '' : 's'}` : 'Cart'}
      >
        <span className="sf-bottom-nav__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="9" cy="20" r="1.4" />
            <circle cx="18" cy="20" r="1.4" />
            <path d="M3 4h2.2l2.5 12.3a2 2 0 0 0 2 1.6h9.1a2 2 0 0 0 2-1.6L22.4 8H6" />
          </svg>
        </span>
        <span className="sf-bottom-nav__label">Cart</span>
        {cartCount > 0 && (
          <span className="sf-bottom-nav__badge" aria-hidden="true">
            {cartCount > 99 ? '99+' : cartCount}
          </span>
        )}
      </button>
    </nav>
  );
};

export default StorefrontBottomNav;
